'use client'

import {
  Menu,
  UnstyledButton,
  Group,
  Text,
  Button,
  Loader,
} from '@mantine/core';
import { 
  IconUserPlus, 
  IconBell,
  IconCalendarTime,
  IconShield, 
  IconUser, 
  IconCreditCard, 
  IconWallet, 
  IconLogout 
} from '@tabler/icons-react';
import { useRouter } from 'next/navigation';
import { signOut } from 'next-auth/react';
import Link from 'next/link';
import classes from './UserMenu.module.css';
import { useCurrentMentor, useCurrentUser } from '@/providers/CurrentUserProvider';
import { Avatar } from './Avatar';

interface UserMenuProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function UserMenu({ isOpen, onToggle }: UserMenuProps) {
  const { user, userLoading } = useCurrentUser();

  const router = useRouter();
  const { mentor } = useCurrentMentor();

  const handleLogout = async () => {
    await signOut({ redirect: false });
    router.push('/auth/sign-in');
    router.refresh();
  }

  // Show loading state
  if (userLoading && !user) {
    return <Loader size="sm" />;
  }

  // Show Sign-in buttons if not logged in
  if (!user) {
    return (
      <Group className={classes.desktopAuthButtons}>
        <Button component={Link} href="/auth/sign-up">Sign Up</Button>
        <Button component={Link} href="/auth/sign-in" variant="outline">Sign In</Button>
      </Group>
    );
  }

  const menuLinks = [];

  if (user.isMentorApproved) {
    // Only show "Create Mentor Profile" if they don't have a profile yet
    if (!mentor?.handle) {
      menuLinks.push({ 
        href: '/account/profile?mentor_profile=true', 
        linkText: 'Create Mentor Profile',
        icon: <IconUserPlus size={14} />
      });
    }
  } else {
    // Not approved yet - show apply link
    menuLinks.push({ 
      href: '/mentoring/apply', 
      linkText: 'Become a Mentor',
      icon: <IconUserPlus size={14} />
    });
  }

  menuLinks.push({ 
    href: '/account/notifications', 
    linkText: 'Notifications',
    icon: <IconBell size={14} />
  });
  
  // Only show availability for approved mentors
  if (user.isMentorApproved) {
    menuLinks.push({ 
      href: '/account/availability', 
      linkText: 'Availability',
      icon: <IconCalendarTime size={14} />
    });
  }
  
  menuLinks.push({ 
    href: '/account/security', 
    linkText: 'Security',
    icon: <IconShield size={14} />
  });
  menuLinks.push({ 
    href: '/account/profile', 
    linkText: 'Profile',
    icon: <IconUser size={14} />
  });
  menuLinks.push({ 
    href: '/account/billing', 
    linkText: 'Billing',
    icon: <IconCreditCard size={14} />
  });
  
  // Show Payouts link only for approved mentors
  if (user.isMentorApproved) {
    menuLinks.push({ 
      href: '/account/payouts', 
      linkText: 'Payouts',
      icon: <IconWallet size={14} />
    });
  }

  const handleMenuItemClick = (href: string) => {
    router.push(href);
    onToggle();
  };

  const handleLogoutClick = async () => {
    await handleLogout();
    onToggle();
  };

  return (
    <Group>
      <Menu shadow="md" width={200} opened={isOpen} onChange={onToggle}>
        <Menu.Target>
          <UnstyledButton className={classes.userButton} style={{ display: 'flex', alignItems: 'center' }}>
            <Avatar radius={50} size={32} className={classes.avatar} userId={user.id} avatarId={user.avatarId} />
            <Text className={classes.userName}>{user.name}</Text>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown className={classes.menuDropdown}>
          {menuLinks.map((link, index) => (
            <Menu.Item 
              key={index} 
              leftSection={link.icon}
              onClick={() => handleMenuItemClick(link.href)}
            >
              {link.linkText}
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item 
            leftSection={<IconLogout size={14} />}
            onClick={handleLogoutClick}
          >
            Log out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
